import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Shield, BookOpen, AlertTriangle, Heart, Building2, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';

export const NABHTraining = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "NABH Training: Patient Safety and Quality Standards in Healthcare",
    "description": "NABH training prepares hospital staff for NABH accreditation standards, covering infection control, patient safety, emergency management, patient rights, and clinical quality across all departments.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy",
      "sameAs": "https://isde.academy"
    },
    "educationalLevel": "All Levels",
    "audience": {
      "@type": "Audience",
      "audienceType": "Doctors, Nurses, Hospital Staff, Quality Managers, Healthcare Administrators"
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "Blended",
      "duration": "P3D"
    },
    "teaches": [
      "Infection control and hand hygiene",
      "Patient safety and IPSG",
      "Emergency management and Code Blue",
      "Patient rights and informed consent",
      "Biomedical waste management",
      "Medication safety",
      "NABH accreditation documentation"
    ],
    "geographicArea": {
      "@type": "Country",
      "name": "India"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is NABH Training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "NABH training refers to structured education and competency-building programmes designed to prepare hospital staff for NABH accreditation standards. It focuses on building a culture of quality and safety across all departments — clinical and non-clinical."
        }
      },
      {
        "@type": "Question",
        "name": "Who should attend NABH training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "NABH training applies to all hospital staff including doctors, nurses, ICU and OT staff, lab and radiology teams, pharmacists, front office, housekeeping, security, biomedical waste handlers, quality managers, HR coordinators, and hospital administrators."
        }
      },
      {
        "@type": "Question",
        "name": "What are the mandatory NABH training topics?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Mandatory NABH training topics include Basic Life Support (BLS), fire safety and mock drills, biomedical waste management, infection control and hand hygiene, incident reporting systems, documentation procedures, patient rights and confidentiality, and medication safety with high-risk drugs."
        }
      },
      {
        "@type": "Question",
        "name": "Is NABH training required for all departments?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. NABH accreditation success depends on the performance of the entire healthcare system — not only clinical care. Training is mandatory for clinical teams, non-clinical support staff, and management teams. Each role receives relevant, department-specific modules."
        }
      },
      {
        "@type": "Question",
        "name": "What is covered in NABH infection control training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "NABH infection control training covers standard precautions, WHO 5 moments of hand hygiene, PPE donning and doffing, biomedical waste colour coding and segregation, CSSD sterilisation and documentation, and needle stick injury management including post-exposure prophylaxis."
        }
      },
      {
        "@type": "Question",
        "name": "How does NABH training help hospitals in India?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "NABH training helps hospitals across India improve patient safety outcomes, reduce clinical errors, strengthen infection prevention, improve documentation compliance, and increase readiness for internal and external accreditation audits. It builds a measurable quality culture required for NABH accreditation."
        }
      }
    ]
  };

  const coreTopics = [
    {
      number: "1",
      title: "Infection Control Practices (NABH Mandatory)",
      icon: Shield,
      color: "blue",
      intro: "Infection control is one of the most critical components of NABH training because healthcare-associated infections (HAIs) are a major risk in hospitals. NABH training ensures staff understand standard precautions and infection prevention protocols.",
      subtopics: [
        {
          name: "Standard Precautions",
          points: ["Universal precautions for all patients", "Safe handling of body fluids and contaminated materials"]
        },
        {
          name: "Hand Hygiene",
          points: ["WHO 5 moments of hand hygiene", "Correct technique and compliance monitoring"]
        },
        {
          name: "PPE Use",
          points: ["Gloves, masks, gowns, face shields", "Proper donning and doffing procedures"]
        },
        {
          name: "Biomedical Waste (BMW) Management",
          points: ["Colour coding and segregation", "Safe disposal procedures", "Department-level compliance practices"]
        },
        {
          name: "Sterilisation and CSSD",
          points: ["Sterilisation methods and monitoring", "Instrument handling and packaging", "Traceability and sterilisation documentation"]
        },
        {
          name: "Needle Stick Injury Management",
          points: ["Prevention protocols", "Reporting systems", "Post-exposure prophylaxis awareness"]
        }
      ]
    },
    {
      number: "2",
      title: "Patient Safety and Quality Standards",
      icon: Heart,
      color: "green",
      intro: "Patient safety is the foundation of NABH. Training in this area focuses on preventing avoidable harm and ensuring safe systems of care.",
      subtopics: [
        {
          name: "International Patient Safety Goals (IPSG)",
          points: ["Correct patient identification", "Effective communication", "Safe medication practices", "Correct procedure and site verification", "Infection prevention", "Fall prevention and safety monitoring"]
        },
        {
          name: "Medication Safety",
          points: ["Common medication errors", "High-risk medications", "LASA drugs (Look-Alike Sound-Alike)", "Safe storage and dispensing procedures", "Safe administration practices"]
        },
        {
          name: "Patient Identification",
          points: ["Two-identifier system", "Identification bands and documentation"]
        },
        {
          name: "Documentation Standards",
          points: ["Clear, legible clinical documentation", "Record keeping compliance", "Consent forms and clinical notes", "Audit-ready documentation procedures"]
        }
      ]
    },
    {
      number: "3",
      title: "Emergency and Facility Management Training",
      icon: AlertTriangle,
      color: "red",
      intro: "NABH training includes emergency management because hospitals must be prepared for clinical emergencies, fire safety risks, and disaster events. Staff must be trained to respond quickly and correctly.",
      subtopics: [
        {
          name: "Code Blue Systems",
          points: ["BLS response protocols", "CPR roles and responsibilities", "Rapid response team awareness"]
        },
        {
          name: "Fire Safety and Fire Drills",
          points: ["Fire prevention systems", "Fire extinguisher usage", "Evacuation procedures", "Mock drills and documentation"]
        },
        {
          name: "Disaster Management",
          points: ["Hospital disaster response plan", "Internal and external disaster scenarios", "Coordination and escalation"]
        },
        {
          name: "Security and Facility Safety",
          points: ["Patient and staff safety", "Restricted access and visitor control", "Facility hazard reporting"]
        }
      ]
    },
    {
      number: "4",
      title: "Patient Rights and Patient Education",
      icon: Award,
      color: "purple",
      intro: "NABH places strong emphasis on ethical care delivery, respect for patient rights, and transparency. NABH training ensures staff understand legal and ethical responsibilities.",
      subtopics: [
        {
          name: "Informed Consent",
          points: ["When consent is required", "Proper consent documentation", "Consent for procedures, surgery, and transfusion"]
        },
        {
          name: "Patient Rights and Responsibilities",
          points: ["Respect and dignity", "Privacy and confidentiality", "Transparent information sharing"]
        },
        {
          name: "Confidentiality",
          points: ["Protecting patient information", "Responsible handling of medical records"]
        },
        {
          name: "Grievance Handling",
          points: ["Complaint registration process", "Resolution workflows", "Documentation and escalation"]
        }
      ]
    },
    {
      number: "5",
      title: "Clinical Care and Operational Standards",
      icon: BookOpen,
      color: "teal",
      intro: "NABH training also includes clinical safety topics that improve hospital quality and reduce clinical risks.",
      subtopics: [
        {
          name: "Care of Vulnerable Patients",
          points: ["Elderly care safety", "Paediatric safety", "High-risk patient monitoring"]
        },
        {
          name: "Safe Medication Administration",
          points: ["Right patient, drug, dose, route, time", "Monitoring adverse drug reactions"]
        },
        {
          name: "Blood Transfusion Safety",
          points: ["Blood compatibility checks", "Transfusion documentation", "Monitoring and reaction management"]
        },
        {
          name: "Infection Control Audits",
          points: ["Hand hygiene audits", "Environmental cleanliness audits", "Surveillance and reporting"]
        }
      ]
    },
    {
      number: "6",
      title: "Administrative and Support Department Training",
      icon: Building2,
      color: "orange",
      intro: "NABH training is incomplete without administrative and support department participation. NABH requires system-wide compliance.",
      subtopics: [
        {
          name: "Human Resource Management",
          points: ["Staff credentialing and onboarding", "Competency assessment", "Training record maintenance"]
        },
        {
          name: "Departmental Training",
          points: ["Role-based training modules", "SOP adherence"]
        },
        {
          name: "Quality Indicator Reporting",
          points: ["Monitoring clinical indicators", "Incident reporting trends", "Department-level performance review"]
        }
      ]
    }
  ];

  const mandatoryModules = [
    "Basic Life Support (BLS)",
    "Fire safety and mock drills",
    "Biomedical waste management",
    "Infection control and hand hygiene",
    "Incident reporting systems",
    "Documentation procedures",
    "Patient rights and confidentiality",
    "Medication safety and high-risk drugs"
  ];

  const targetAudience = [
    {
      category: "Clinical Teams",
      icon: Heart,
      members: ["Doctors", "Nurses", "ICU and OT staff", "Lab and radiology teams", "Pharmacists"]
    },
    {
      category: "Non-Clinical Teams",
      icon: Building2,
      members: ["Front office and billing", "Housekeeping and sanitation", "Biomedical waste handlers", "Security teams", "Facility management"]
    },
    {
      category: "Management Teams",
      icon: Users,
      members: ["Quality managers", "HR and training coordinators", "Department heads", "Hospital administrators"]
    }
  ];

  const colorMap = {
    blue: { bg: "bg-blue-50", border: "border-blue-200", icon: "text-blue-600", badge: "bg-blue-100 text-blue-700" },
    green: { bg: "bg-green-50", border: "border-green-200", icon: "text-green-600", badge: "bg-green-100 text-green-700" },
    red: { bg: "bg-red-50", border: "border-red-200", icon: "text-red-600", badge: "bg-red-100 text-red-700" },
    purple: { bg: "bg-purple-50", border: "border-purple-200", icon: "text-purple-600", badge: "bg-purple-100 text-purple-700" },
    teal: { bg: "bg-teal-50", border: "border-teal-200", icon: "text-teal-600", badge: "bg-teal-100 text-teal-700" },
    orange: { bg: "bg-orange-50", border: "border-orange-200", icon: "text-orange-600", badge: "bg-orange-100 text-orange-700" }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="NABH Training: Patient Safety and Quality Standards in Healthcare | ISDE Academy"
        description="Comprehensive NABH training for hospitals and healthcare organisations in India. Covers infection control, patient safety, emergency management, and NABH accreditation compliance. Enrol now."
        keywords="NABH training, NABH accreditation training india, hospital quality training, patient safety training, infection control training, healthcare quality standards, NABH compliance training, hospital staff training india, Chennai"
        canonical="https://isde.academy/nabh-training"
        ogUrl="https://isde.academy/nabh-training"
        structuredData={[structuredData, faqStructuredData]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700 text-white py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Button
              variant="ghost"
              className="text-blue-200 hover:text-white mb-6 p-0"
              onClick={() => navigate('/industry-training/healthcare')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Healthcare Training
            </Button>

            <div className="flex items-center gap-3 mb-4">
              <span className="bg-blue-500 bg-opacity-40 text-blue-100 text-sm font-medium px-3 py-1 rounded-full">
                Healthcare Quality
              </span>
              <span className="bg-teal-500 bg-opacity-40 text-teal-100 text-sm font-medium px-3 py-1 rounded-full">
                NABH Accreditation
              </span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              NABH Training: A Complete Guide to Patient Safety and Quality Standards in Healthcare
            </h1>

            <p className="text-lg md:text-xl text-blue-100 mb-4 max-w-4xl">
              NABH training is one of the most important steps for hospitals and healthcare organisations aiming to strengthen patient safety, improve clinical quality, and achieve NABH accreditation compliance. NABH (National Accreditation Board for Hospitals &amp; Healthcare Providers) standards are designed to ensure healthcare institutions deliver safe, ethical, consistent, and well-documented care.
            </p>
            <p className="text-base md:text-lg text-blue-200 max-w-4xl">
              In today's healthcare environment, quality is no longer optional. Patients, families, regulatory bodies, and healthcare partners expect hospitals to follow standardised systems that reduce risks, improve outcomes, and ensure accountability. This is exactly what NABH training supports.
            </p>

            <div className="flex flex-wrap gap-4 mt-8">
              <Button
                size="lg"
                className="bg-white text-blue-900 hover:bg-blue-50 font-semibold"
                onClick={() => navigate('/contact')}
              >
                Enquire Now
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-blue-900 font-semibold"
                onClick={() => navigate('/training-calendar')}
              >
                View Schedule
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is NABH Training */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">What is NABH Training?</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                NABH training refers to structured education and competency-building programmes designed to prepare hospital staff for NABH accreditation standards. The training focuses on building a culture of quality and safety across all departments.
              </p>
              <p>
                Unlike general healthcare training, NABH training is compliance-driven and standard-based. It ensures that hospital processes are aligned with NABH guidelines and that staff follow consistent procedures in daily operations.
              </p>
              <p>
                NABH training is not limited to doctors and nurses. It applies to all staff, including housekeeping, front office, security, biomedical teams, and management. This is because accreditation success depends on the performance of the entire healthcare system — not only clinical care.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why NABH Training is Important */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Why NABH Training is Important for Hospitals</h2>
            <p className="text-gray-700 mb-8 text-lg">
              NABH accreditation requires more than policies and checklists. It requires staff awareness, consistent practice, and measurable compliance. NABH training ensures that staff can apply quality standards in real-world situations.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-5">Key Benefits of NABH Training</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { text: "Improved patient safety outcomes", icon: Heart },
                { text: "Reduced clinical errors and incidents", icon: Shield },
                { text: "Stronger infection prevention systems", icon: CheckCircle },
                { text: "Better emergency preparedness", icon: AlertTriangle },
                { text: "Higher compliance with documentation standards", icon: BookOpen },
                { text: "Improved patient satisfaction and trust", icon: Award },
                { text: "Increased readiness for internal and external audits", icon: CheckCircle },
                { text: "System-wide quality culture across all departments", icon: Building2 }
              ].map((benefit, idx) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.05 }}
                    className="bg-white rounded-lg p-4 border border-gray-200 flex items-start gap-3 shadow-sm"
                  >
                    <Icon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-sm font-medium">{benefit.text}</p>
                  </motion.div>
                );
              })}
            </div>

            <p className="text-gray-600 mt-6">
              Hospitals that invest in NABH training build a more resilient healthcare system where staff follow standardised processes, risks are reduced, and quality becomes measurable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Topics */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Core Topics Covered in NABH Training</h3>
            <p className="text-gray-600 text-lg">
              NABH training covers multiple domains of hospital quality, patient safety, clinical operations, and facility management. Below are the most essential areas included in NABH training programmes.
            </p>
          </motion.div>

          <div className="space-y-10">
            {coreTopics.map((topic, idx) => {
              const colors = colorMap[topic.color];
              const Icon = topic.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className={`rounded-xl border ${colors.border} ${colors.bg} p-6 md:p-8`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colors.badge}`}>
                      <span className="font-bold text-sm">{topic.number}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon className={`w-6 h-6 ${colors.icon}`} />
                        <h4 className="text-xl font-bold text-gray-900">{topic.title}</h4>
                      </div>
                      <p className="text-gray-700">{topic.intro}</p>
                    </div>
                  </div>

                  <div className="ml-14 grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {topic.subtopics.map((sub, sIdx) => (
                      <div key={sIdx} className="bg-white bg-opacity-70 rounded-lg p-4 border border-white">
                        <h4 className="font-semibold text-gray-800 mb-2">{sub.name}</h4>
                        <ul className="space-y-1">
                          {sub.points.map((point, pIdx) => (
                            <li key={pIdx} className="flex items-start gap-2 text-sm text-gray-600">
                              <CheckCircle className={`w-4 h-4 ${colors.icon} flex-shrink-0 mt-0.5`} />
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mandatory Modules */}
      <section className="py-14 bg-blue-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-3">Mandatory NABH Training Topics (Must-Have Modules)</h2>
            <p className="text-blue-200 mb-8">
              Most hospitals include these mandatory modules as part of NABH training. These topics are essential for NABH compliance and must be trained regularly, not just once.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {mandatoryModules.map((module, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.07 }}
                  className="bg-blue-800 bg-opacity-60 border border-blue-700 rounded-lg p-4 flex items-start gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-blue-100">{module}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">NABH Training Target Audience (Who Should Be Trained?)</h2>
            <p className="text-gray-600 mb-8 text-lg">
              NABH training applies to all hospital staff. NABH training should also include role-specific modules, ensuring that staff receive training relevant to their responsibilities.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {targetAudience.map((group, idx) => {
                const Icon = group.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Card className="h-full border-0 shadow-md">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Icon className="w-5 h-5 text-blue-600" />
                          </div>
                          <h3 className="text-lg font-bold text-gray-900">{group.category}</h3>
                        </div>
                        <ul className="space-y-2">
                          {group.members.map((member, mIdx) => (
                            <li key={mIdx} className="flex items-center gap-2 text-gray-700 text-sm">
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              {member}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions About NABH Training</h2>
            <div className="space-y-5">
              {faqStructuredData.mainEntity.map((faq, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-5">
                  <h3 className="font-semibold text-gray-900 mb-2">{faq.name}</h3>
                  <p className="text-gray-600 text-sm">{faq.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Courses */}
      <RelatedCoursesSection currentSlug="iso-9001" slugs={['iso-9001', 'iso-45001', 'iso-13485']} heading="Related ISO Training Courses" />

      {/* CTA */}
      <section className="py-14 bg-gradient-to-r from-blue-800 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Begin NABH Training?</h2>
            <p className="text-blue-200 mb-8 text-lg">
              Contact ISDE Academy to enrol your hospital team in structured NABH training programmes. We deliver training across India including Chennai, Bangalore, Mumbai, and Hyderabad — on-site, classroom, and hybrid modes available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-blue-900 hover:bg-blue-50 font-semibold"
                onClick={() => navigate('/contact')}
              >
                Enquire About NABH Training
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-blue-900 font-semibold"
                onClick={() => navigate('/training-calendar')}
              >
                View Training Calendar
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
